/**
 * LA MORT, LA REPRISE, ET LA FIN.
 *
 * « Un écran, et un seul, tenu. Le nom, l'archétype, la cause, l'heure de jeu, la carte de ce qui a
 * été exploré, le journal. On ne propose PAS de recommencer dans les trois secondes : la mort a
 * besoin d'un temps mort, sinon elle ne coûte rien » (design §3).
 *
 * D'où le délai avant que les issues n'apparaissent. Ce n'est pas une animation : c'est du temps,
 * et il reste là même sous `prefers-reduced-motion` — ce qu'on retire à un joueur qui craint le
 * mouvement, c'est le fondu, pas le deuil.
 */
import { useEffect, useMemo, useState } from "react";

import { api } from "../../api/client.js";
import { messageDe } from "../../lexique/erreurs.js";

import type { CarteEnVue, Caracteristique, LigneDeJournal, PartieEnVue } from "../../api/types.js";
import { CarteDuMonde } from "../../composants/CarteDuMonde.js";
import { Lettrine } from "../../composants/Lettrine.js";
import { Sceau } from "../../composants/Sceau.js";
import { Filet, Parchemin } from "../../composants/Parchemin.js";
import { ARCHETYPES, CARACTERISTIQUES, ORDRE_DES_CARACTERISTIQUES, dureeDite } from "../../lexique/mots.js";
import { Lien } from "../../routeur.js";
import { paragraphesDe } from "../../lecture/feuillets.js";
import { ditLaLigne } from "../../lexique/journal.js";
import { t } from "../../lexique/langue.js";

const TEMPS_MORT = 6000;

function useTempsMort(): boolean {
  const [passe, posePasse] = useState(false);
  useEffect(() => {
    const minuteur = window.setTimeout(() => posePasse(true), TEMPS_MORT);
    return () => window.clearTimeout(minuteur);
  }, []);
  return passe;
}

/**
 * La cause est la dernière ligne du journal : le serveur l'y écrit au moment de la mort, et c'est
 * la seule source qui ne se perde pas.
 *
 * ELLE SE LIT AU CODE, PLUS À LA PHRASE (§13.1). On cherchait « Mort : » en tête de résumé — une
 * lecture de français qui ne survivait ni à une reformulation ni à une traduction. Le paramètre
 * `cause`, lui, est une citation du serveur et reste tel quel. Les parties d'avant la migration 013
 * gardent leur vieille phrase : on la découpe comme avant, faute de mieux.
 */
function causeDeLaMort(journal: LigneDeJournal[]): string | null {
  for (let i = journal.length - 1; i >= 0; i -= 1) {
    const ligne = journal[i]!;
    if (ligne.code === "mort") return String(ligne.params.cause ?? "");
    if (!ligne.code && ligne.resume?.startsWith("Mort :")) {
      return ligne.resume.replace(/^Mort\s*:\s*/, "").replace(/\.$/, "");
    }
  }
  return null;
}

/**
 * PARTAGER SON CARNET (§8.1, design §3). Il n'apparaît qu'une fois la partie close — c'est le
 * serveur qui le tient, et l'écran de fin est le seul endroit où le bouton existe. Le proposer
 * pendant la partie aurait été proposer de publier la solution.
 *
 * ET IL EST DANS LES ISSUES, donc APRÈS le temps mort de la mort : proposer de raconter sa défaite
 * dans les trois secondes qui la suivent, c'est exactement le contraire de ce que ce délai protège.
 */
function LePartage({ partieId }: { partieId: string }) {
  const [jeton, poseLeJeton] = useState<string | null>(null);
  const [su, poseLeSu] = useState(false);
  const [refus, poseLeRefus] = useState<string | null>(null);
  const [enCours, poseEnCours] = useState(false);

  // On demande d'abord s'il y en a déjà un : republier tirerait un NOUVEAU jeton, et le lien donné
  // il y a trois semaines cesserait de marcher sans qu'on l'ait voulu.
  useEffect(() => {
    let vivant = true;
    api
      .lis<{ jeton: string | null }>(`/parties/${partieId}/partage`)
      .then((r) => vivant && (poseLeJeton(r.jeton), poseLeSu(true)))
      .catch(() => vivant && poseLeSu(true));
    return () => {
      vivant = false;
    };
  }, [partieId]);

  const lien = jeton ? `${window.location.origin}/carnet/${jeton}` : null;

  async function bascule() {
    poseLeRefus(null);
    poseEnCours(true);
    try {
      if (jeton) {
        await api.envoie("DELETE", `/parties/${partieId}/partage`);
        poseLeJeton(null);
      } else {
        poseLeJeton((await api.poste<{ jeton: string }>(`/parties/${partieId}/partage`)).jeton);
      }
    } catch (erreur) {
      poseLeRefus(messageDe(erreur));
    } finally {
      poseEnCours(false);
    }
  }

  if (!su) return null;
  return (
    <div className="partage">
      {lien ? (
        <>
          {/* LE LIEN SE LIT ET SE COPIE. Pas de bouton « partager sur… » : on ne sait pas où la
              personne veut l'envoyer, et un lien en clair marche partout. */}
          <input className="partage__lien" readOnly value={lien} onFocus={(e) => e.currentTarget.select()} aria-label={t("Lien du carnet")} />
          <p className="faible" style={{ margin: 0 }}>
            {t("Qui a ce lien lit le carnet — le nom, la fin, la carte et le journal. Jamais le texte des nœuds : on raconte un parcours, on ne prête pas l'aventure.")}
          </p>
        </>
      ) : (
        <p className="faible" style={{ margin: 0 }}>
          {t("Douze heures de jeu tiennent dans une colonne de phrases. On peut les donner à lire.")}
        </p>
      )}
      {refus ? <p className="mention mention--echec">{refus}</p> : null}
      <button type="button" className="bouton bouton--discret" disabled={enCours} onClick={() => void bascule()}>
        {jeton ? t("Ne plus partager") : t("Partager ce carnet")}
      </button>
    </div>
  );
}

export function Mort({ partie, journal, carte }: { partie: PartieEnVue; journal: LigneDeJournal[]; carte: CarteEnVue | null }) {
  const passe = useTempsMort();
  const cause = causeDeLaMort(journal);
  const { heros, horloge } = partie;

  return (
    <div className="page page--etroite epitaphe arrivee">
      <div className="epitaphe__sceau">
        <Sceau empreinte="mort" taille={92} titre={t("Mort")} />
      </div>

      <h1 className="epitaphe__nom">{heros.nom}</h1>
      <p className="epitaphe__ligne capitales">
        {cause
          ? t("{archetype} — mort : {cause}", { archetype: ARCHETYPES[heros.archetype]?.nom ?? heros.archetype, cause })
          : t("{archetype} — mort en chemin", { archetype: ARCHETYPES[heros.archetype]?.nom ?? heros.archetype })}
      </p>

      <Filet />

      <dl className="epitaphe__bilan">
        <div>
          <dt className="capitales">{t("Il aura tenu")}</dt>
          <dd className="nombre">{dureeDite(horloge.heures)}</dd>
        </div>
        <div>
          <dt className="capitales">{t("Tombé le jour")}</dt>
          <dd className="nombre">{horloge.jour}</dd>
        </div>
        <div>
          <dt className="capitales">{t("Armé à")}</dt>
          <dd className="nombre">{partie.ipe}</dd>
        </div>
        <div>
          <dt className="capitales">{t("Sur lui")}</dt>
          <dd className="nombre">{t("{or} d'or", { or: heros.or })}</dd>
        </div>
      </dl>

      <p className="epitaphe__mot">
        {t("Il ne reviendra pas. Son nom est au cimetière, et une trace reste dans le monde, à l'endroit exact où il est tombé — les prochains la trouveront.")}
      </p>

      {carte ? (
        <Parchemin variante="creux" titre={t("Ce qu'il aura vu")}>
          <CarteDuMonde carte={carte} hauteur={320} />
        </Parchemin>
      ) : null}

      <Parchemin titre={t("Son journal")}>
        <ol className="journal">
          {journal.map((ligne, index) => (
            <li key={index}>
              <span className="journal__heure nombre">{dureeDite(ligne.heure)}</span>
              <span>{ditLaLigne(ligne)}</span>
            </li>
          ))}
        </ol>
      </Parchemin>

      <div className={passe ? "epitaphe__issues epitaphe__issues--offertes" : "epitaphe__issues"} aria-hidden={!passe}>
        <Lien vers="/heros/nouveau" className="bouton bouton--grand">
          {t("Un autre héros")}
        </Lien>
        <Lien vers="/cimetiere" className="bouton">
          {t("Le cimetière")}
        </Lien>
      </div>

      {passe ? <LePartage partieId={partie.id} /> : null}
    </div>
  );
}

export function Reprise({
  partie,
  journal,
  bloque,
  surReprise
}: {
  partie: PartieEnVue;
  journal: LigneDeJournal[];
  bloque: boolean;
  surReprise: (cicatrice: Caracteristique) => void;
}) {
  const cause = causeDeLaMort(journal);
  const dejaPortees = partie.heros.cicatrices;

  return (
    <div className="page page--etroite arrivee">
      <div className="epitaphe__sceau">
        <Sceau empreinte="chronique" taille={84} titre={t("Chronique")} />
      </div>

      <h1 className="epitaphe__nom">{t("{heros} est tombé", { heros: partie.heros.nom })}</h1>
      <p className="epitaphe__ligne capitales">{cause ?? t("en chemin")}</p>

      <Parchemin variante="leve">
        <p style={{ marginTop: 0 }}>
          {t("La Chronique vous laisse repartir de la dernière ancre —")} <strong>{t("une fois de plus")}</strong>{t(", et à un prix : une cicatrice, c'est-à-dire")} <strong>{t("−1 définitif")}</strong> {t("sur une caractéristique de votre choix. Trois au maximum, et ensuite ce mode se ferme.")}
        </p>
        <p className="faible">
          {t("Ce choix ne se tire pas au sort à votre place : c'est le vôtre, il est irréversible, et il vous suivra jusqu'à la fin de ce héros.")}
        </p>

        <div className="cicatrices">
          {ORDRE_DES_CARACTERISTIQUES.map((c) => {
            const mot = CARACTERISTIQUES[c];
            const deja = dejaPortees.filter((x) => x === c).length;
            const valeur = partie.heros.statsEffectives[c];
            return (
              <button
                key={c}
                type="button"
                className="cicatrice"
                disabled={bloque || valeur <= 1}
                onClick={() => surReprise(c)}
              >
                <span className="cicatrice__nom" style={{ color: mot.teinte }}>
                  {mot.nom}
                </span>
                <span className="nombre">
                  {valeur} → {valeur - 1}
                </span>
                {deja ? <span className="faible">{t("déjà entaillée {combien}×", { combien: deja })}</span> : null}
              </button>
            );
          })}
        </div>
        <p className="faible" style={{ marginBottom: 0 }}>
          {t("Reprises déjà faites :")} <span className="nombre">{partie.reprises}</span>{" "}
          {t("· cicatrices portées :")} <span className="nombre">{dejaPortees.length}</span> {t("sur 3.")}
        </p>
      </Parchemin>
    </div>
  );
}

/**
 * LE DÉNOUEMENT, ÉCRIT POUR CETTE AVENTURE-LÀ (§3.5).
 *
 * Un nœud de fin est un nœud SIGNATURE : la passe 7 lui écrit deux cent cinquante à trois cent
 * cinquante mots, et il porte un titre parmi les quatre que la passe 4 a prévus. Rien de tout cela
 * n'était affiché — on posait le pied dessus, la partie se fermait, et l'écran basculait sur une
 * épitaphe identique pour toutes les aventures. Le paiement de quarante heures de jeu était
 * anonyme.
 *
 * IL SE LIT ICI ET NON EN MARCHANT. La partie se clôt bien à l'arrivée (le nœud n'a pas de sortie,
 * il n'y a rien à décider), et c'est sur l'écran de fin qu'on donne à lire ce qui a été écrit — un
 * épilogue se lit après coup.
 *
 * LE TITRE ET LE TEXTE SONT DES CITATIONS (§13.1) : ils viennent de l'aventure et s'affichent tels
 * quels. Rien ici ne se traduit, sauf le cadre.
 */
function LeDenouement({ noeud }: { noeud: PartieEnVue["noeud"] }) {
  // UNE AVENTURE PEUT NE PAS EN AVOIR. Une partie close autrement — un import ancien, un nœud
  // effacé — n'a pas de dénouement à lire, et l'écran doit tenir sans lui.
  const paragraphes = useMemo(() => (noeud ? paragraphesDe(noeud.texte) : []), [noeud]);
  if (!noeud || !paragraphes.length) return null;

  return (
    <Parchemin variante="leve" titre={noeud.titre}>
      <div className="denouement">
        {paragraphes.map((paragraphe, index) =>
          index === 0 ? (
            <Lettrine key={index} texte={paragraphe} />
          ) : (
            <p className="noeud__texte" key={index}>
              {paragraphe}
            </p>
          )
        )}
      </div>
    </Parchemin>
  );
}

export function FinHeureuse({ partie, journal, carte }: { partie: PartieEnVue; journal: LigneDeJournal[]; carte: CarteEnVue | null }) {
  return (
    <div className="page page--etroite arrivee">
      <div className="epitaphe__sceau">
        <Sceau empreinte="fin" taille={92} titre={t("Aventure terminée")} />
      </div>
      <h1 className="epitaphe__nom">{t("{heros} en est sorti", { heros: partie.heros.nom })}</h1>
      <p className="epitaphe__ligne capitales">
        {t("{duree} de jeu — jour {jour}", {
          duree: dureeDite(partie.horloge.heures),
          jour: partie.horloge.jour
        })}
      </p>

      {/* CE QUE L'AVENTURE AVAIT ÉCRIT POUR CE DÉNOUEMENT-CI. Il passe AVANT le mot du jeu : on
          finit de lire l'histoire, puis on referme le livre. */}
      <LeDenouement noeud={partie.noeud} />

      <p className="epitaphe__mot">
        {t("L'aventure est finie, et le héros est vivant. Il gardera ce qu'il porte : c'est avec ça qu'il entrera dans la suivante — et c'est aussi ce qui décidera de celles où on le laissera entrer.")}
      </p>

      {carte ? (
        <Parchemin variante="creux" titre={t("La carte, telle qu'il l'a levée")}>
          <CarteDuMonde carte={carte} hauteur={340} />
        </Parchemin>
      ) : null}

      <Parchemin titre={t("Le journal")}>
        <ol className="journal">
          {journal.map((ligne, index) => (
            <li key={index}>
              <span className="journal__heure nombre">{dureeDite(ligne.heure)}</span>
              <span>{ditLaLigne(ligne)}</span>
            </li>
          ))}
        </ol>
      </Parchemin>

      <div className="epitaphe__issues epitaphe__issues--offertes">
        <Lien vers="/aventures" className="bouton bouton--grand">
          {t("Une autre aventure")}
        </Lien>
        <Lien vers={`/heros/${partie.heros.id}`} className="bouton">
          {t("Sa fiche")}
        </Lien>
      </div>

      <LePartage partieId={partie.id} />
    </div>
  );
}
